import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { array, date, mixed, object, ref } from 'yup';
import { daysOfWeek, type Day } from '~/components/schedule/lib/helpers';
import { DeviceSignalFrequency } from '~/generated/graphql';
import { i18n } from '~/lib/i18n';
import type { NetworkDeviceSignals__SignalSchedule as SignalSchedule } from '../../queries/list.generated';

type EndType = 'Never' | 'On date';

const scheduleModes = ['NONE', ...Object.values(DeviceSignalFrequency)] as const;
type ScheduleMode = (typeof scheduleModes)[number];

interface SignalScheduleFormValues {
  endType: EndType;
  endsOn: DateTime | null;
  frequency: ScheduleMode;
  startTime: DateTime | null;
  startsOn: DateTime | null;
  weekDays: readonly Day[];
}

export const toRunAt = (startsOn: DateTime | null, startTime: DateTime | null) =>
  startsOn && startTime
    ? startsOn.startOf('day').set({ hour: startTime.hour, minute: startTime.minute })
    : null;

export const isScheduled = (frequency: ScheduleMode) => frequency !== 'NONE';
export const repeats = (frequency: ScheduleMode) =>
  isScheduled(frequency) && frequency !== DeviceSignalFrequency.Once;

const dayToDow = (day: Day): number => (daysOfWeek.indexOf(day) + 1) % 7;
const dowToDay = (dow: number): Day => daysOfWeek[(dow + 6) % 7];

const initialValuesFor = (
  schedule: SignalSchedule | null | undefined,
): SignalScheduleFormValues => ({
  endType: schedule?.endsOn != null ? 'On date' : 'Never',
  endsOn: schedule?.endsOn ? DateTime.fromISO(schedule.endsOn) : null,
  frequency: schedule?.frequency ?? 'NONE',
  startTime: schedule?.startTime != null ? DateTime.fromSQL(schedule.startTime) : DateTime.now(),
  startsOn: schedule?.startsOn
    ? DateTime.fromISO(schedule.startsOn)
    : DateTime.now().startOf('day'),
  weekDays: schedule?.weekdays?.length ? schedule.weekdays.map(dowToDay) : daysOfWeek,
});

export const serializeSchedule = (values: SignalScheduleFormValues) => {
  if (!isScheduled(values.frequency)) return null;

  return {
    endsOn:
      repeats(values.frequency) && values.endType === 'On date'
        ? values.endsOn?.toFormat('yyyy-MM-dd') ?? null
        : null,
    frequency: values.frequency as DeviceSignalFrequency,
    startTime: values.startTime?.toFormat('HH:mm') ?? '',
    startsOn: values.startsOn?.toFormat('yyyy-MM-dd') ?? '',
    weekdays:
      values.frequency === DeviceSignalFrequency.Weekly
        ? values.weekDays.map(dayToDow).sort((a, b) => a - b)
        : null,
  };
};

const validationSchema = object({
  endType: mixed<EndType>().oneOf(['Never', 'On date']).required(),
  endsOn: date()
    .nullable()
    .when(['frequency', 'endType'], {
      is: (frequency: ScheduleMode, endType: EndType) =>
        repeats(frequency) && endType === 'On date',
      then: (s) =>
        s
          .required(i18n.t('pages.device_signals.schedule_section.validation.end_required'))
          .min(
            ref('startsOn'),
            i18n.t('pages.device_signals.schedule_section.validation.end_after_start'),
          ),
    }),
  frequency: mixed<ScheduleMode>().oneOf(scheduleModes).required(),
  startTime: date()
    .nullable()
    .when('frequency', { is: isScheduled, then: (s) => s.required() }),
  startsOn: date()
    .nullable()
    .when('frequency', { is: isScheduled, then: (s) => s.required() }),
  weekDays: array().when('frequency', {
    is: DeviceSignalFrequency.Weekly,
    then: (s) => s.min(1).required(),
  }),
});

export const useSchedule = (schedule: SignalSchedule | null | undefined) => {
  const initialValues = useMemo(() => initialValuesFor(schedule), [schedule]);

  return useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: () => undefined,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema,
  });
};

export type SignalScheduleFormik = ReturnType<typeof useSchedule>;
